// Add keys to the appropriate group when their configuration moves elsewhere.
// This file controls edit ownership only. It never deletes data or sets prices.
export const EXTRA_OWNERS = Object.freeze({
  SERVICES: 'services',
  SCHEDULE: 'schedule',
  MOTORCYCLE: 'motorcycle',
  EXTERNAL: 'external',
});

export const WEEKEND_EXTRAS = [
  { key: 'saturday-pick-up', id: 'ca9a4295-fab2-4ed2-aae4-f502e3680d33', day: 'Saturday', action: 'Pick-up' },
  { key: 'saturday-drop-off', id: 'c58b562f-d2d2-470c-b020-5b16b6a63855', day: 'Saturday', action: 'Drop-off' },
  { key: 'sunday-pick-up', id: '50610929-1f25-44e1-a7c4-5ddb31f5d604', day: 'Sunday', action: 'Pick-up' },
  { key: 'sunday-drop-off', id: '19f8cc33-b9a5-4091-b226-699c69d6d834', day: 'Sunday', action: 'Drop-off' },
];

export const EXTRA_OWNERSHIP_RULES = [
  {
    owner: EXTRA_OWNERS.SCHEDULE,
    keys: WEEKEND_EXTRAS.map(item => item.key),
    fallbackIds: WEEKEND_EXTRAS.map(item => item.id),
  },
  {
    owner: EXTRA_OWNERS.MOTORCYCLE,
    keys: [
      'cdw', 'cdw2',
      'free_unlimited_after_days', 'free-unlimited-after-days',
      'unlimited_per_day', 'unlimited-per-day',
      'limited_km_per_day', 'limited-km-per-day',
      'third-party-insurance',
    ],
    fallbackIds: [
      'df04c9c4-d4cc-4e9f-b7b8-bad02d056d15',
      '2c9e2d34-ef24-4762-9e10-e8e5cca6e933',
      '25c0e7a3-0e74-4c54-82df-552a7d2bcca5',
      'c02201ba-9404-42de-9043-ec39c83bf8a2',
    ],
  },
  {
    owner: EXTRA_OWNERS.EXTERNAL,
    // Add actual keys here for extras configured on another screen.
    // Example after verifying the real catalog key: 'your-extra-key'
    keys: ['ferry-tickets','border-crossing','after-hours-service','bike-allowed-on-ferry','drop-off-at-another-location'],
    fallbackIds: [
      '3afec6d6-a77e-4960-9943-2dfd26d7b04b',
      '4db241ef-a3d6-4524-a929-705cc2904ad0',
      '80a190f5-dc32-4072-b8a5-e7989f396bc5',
      '0c09e7b3-9804-4899-b8cb-c88c002f0ce5',
      'c8feb3ea-2457-43c7-8ee1-ba8a1990d53d'
    ],
  },
];

export const normalizeExtraKey = value => String(value ?? '').trim().toLowerCase();
export const getExtraId = item => String(item?.extraId ?? item?.Extra?.id ?? item?.id ?? '');
export const normalizeCatalogList = catalog => {
  if (Array.isArray(catalog)) return catalog;
  if (Array.isArray(catalog?.extras)) return catalog.extras;
  if (catalog && typeof catalog === 'object' && catalog.id) return [catalog];
  return [];
};

function compileRules(rules) {
  const ownerByKey = new Map();
  const fallbackOwnerById = new Map();
  for (const rule of rules) {
    for (const key of rule.keys ?? []) {
      const normalized = normalizeExtraKey(key);
      if (ownerByKey.has(normalized) && ownerByKey.get(normalized) !== rule.owner) {
        throw new Error(`Conflicting extra ownership: ${normalized}`);
      }
      ownerByKey.set(normalized, rule.owner);
    }
    for (const id of rule.fallbackIds ?? []) {
      const normalized = String(id);
      if (fallbackOwnerById.has(normalized) && fallbackOwnerById.get(normalized) !== rule.owner) {
        throw new Error(`Conflicting extra ownership ID: ${normalized}`);
      }
      fallbackOwnerById.set(normalized, rule.owner);
    }
  }
  return { ownerByKey, fallbackOwnerById };
}

const DEFAULT_RULE_INDEX = compileRules(EXTRA_OWNERSHIP_RULES);

// Build once per catalog change. Classification is O(1) per row.
// The optional rules argument is useful for tests or another explicit rule set.
export function buildExtraOwnershipIndex(catalog, rules = EXTRA_OWNERSHIP_RULES) {
  const list = normalizeCatalogList(catalog).filter(item => item?.id);
  const catalogById = new Map(list.map(item => [String(item.id), item]));
  const catalogByKey = new Map(list.map(item => [normalizeExtraKey(item.key), item]));
  const ruleIndex = rules === EXTRA_OWNERSHIP_RULES ? DEFAULT_RULE_INDEX : compileRules(rules);
  const getOwner = item => {
    const id = getExtraId(item);
    const catalogItem = catalogById.get(id);
    const key = normalizeExtraKey(catalogItem?.key || item?.key || item?.Extra?.key);
    return ruleIndex.ownerByKey.get(key)
      ?? ruleIndex.fallbackOwnerById.get(id)
      ?? EXTRA_OWNERS.SERVICES;
  };
  const visibleCatalog = list.filter(item => getOwner(item) === EXTRA_OWNERS.SERVICES);
  return { catalogById, catalogByKey, getOwner, visibleCatalog };
}

// Update the complete authoritative array. Unrelated rows retain all metadata.
// Hidden rows cannot be edited through a Services action.
export function updateServiceExtras(currentRows, ownershipIndex, action) {
  const rows = Array.isArray(currentRows) ? currentRows : [];
  const id = String(action.extraId ?? '');
  if (!id) return rows;
  if (action.type === 'add') {
    const catalogItem = ownershipIndex.catalogById.get(id);
    if (!catalogItem || ownershipIndex.getOwner(catalogItem) !== EXTRA_OWNERS.SERVICES) return rows;
    if (rows.some(row => getExtraId(row) === id)) return rows;
    return [...rows, {
      extraId: id, priceType: 'PER_DAY', pricePerDay: 0,
      maxPrice: 0, isRequired: false, description: '',
    }];
  }
  if (action.type === 'remove') {
    return rows.filter(row => getExtraId(row) !== id
      || ownershipIndex.getOwner(row) !== EXTRA_OWNERS.SERVICES);
  }
  if (action.type === 'patch') {
    return rows.map(row => {
      if (getExtraId(row) !== id || ownershipIndex.getOwner(row) !== EXTRA_OWNERS.SERVICES) return row;
      const next = { ...row };
      // Do not allow an edit to replace persisted IDs or ownership metadata.
      for (const key of ['priceType', 'pricePerDay', 'maxPrice', 'isRequired', 'description']) {
        if (Object.prototype.hasOwnProperty.call(action.patch ?? {}, key)) next[key] = action.patch[key];
      }
      if (action.patch?.priceType === 'ONE_TIME') next.maxPrice = null;
      return next;
    });
  }
  return rows;
}
